// Full-screen in-call UI for the lawyer. Reuses the client's call styles and
// Agora hook. Per the call design the lawyer has no End Call control — they do
// not hang up on a consultation the client paid for. The call ends when:
//   - the client hangs up (call-ended, handled by LawyerCallManager),
//   - the paid plan runs out (auto-end here),
//   - the client drops out of the channel and doesn't come back,
//   - or this tab is closed (reported with a keepalive request).
import React, { useEffect, useRef, useState } from "react";
import { useAgora } from "../../call/useAgora";
import VideoPlayer from "../../call/VideoPlayer";
import socketService from "../../../services/SocketService";
import { lawyerAuth, lawyerService } from "../../../services/lawyerApi";
import brandLogo from "../../../assets/images/footer-logo.png";
import "../../call/call.css";

// How long the client may be gone from the channel (network blip, reload)
// before we treat the call as over.
const LEFT_GRACE_MS = 30 * 1000;

const fmt = (total) => {
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

// Survives the page going away, unlike an axios call.
const reportEndOnUnload = (callId) => {
  try {
    fetch(`${process.env.REACT_APP_API_URL}/lawyer/call/end`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${lawyerAuth.getToken()}`,
      },
      body: JSON.stringify({ callId }),
    });
  } catch {
    /* page is going away regardless */
  }
};

export default function LawyerCallScreen({ call, onEnd }) {
  const {
    status, error, remoteUsers, localVideoTrack,
    micOn, camOn, speakerOn, canFlip, playbackDevices, activeSpeaker,
    toggleMic, toggleCam, toggleSpeaker, flipCamera, setPlayback, setAway,
  } = useAgora(call.creds);

  const [seconds, setSeconds] = useState(0);
  const [heldByThem, setHeldByThem] = useState(false);
  const [warnVisible, setWarnVisible] = useState(false);
  const [spkMenuOpen, setSpkMenuOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const localRef = useRef(null);
  const warnedRef = useRef(false);
  const endedRef = useRef(false);
  const seenRemoteRef = useRef(false);
  // Minutes the client paid to add to a free call (socket `call-extended`) —
  // 0 unless that happened, so `total` is just the plan length as before.
  const [extraMinutes, setExtraMinutes] = useState(0);
  const [extendedNotice, setExtendedNotice] = useState(null);
  const extraRef = useRef(0);
  extraRef.current = extraMinutes;
  const expiryCheckRef = useRef(false);

  const applyExtension = (minutes) => {
    setExtraMinutes((cur) => cur || minutes);
    warnedRef.current = false;
    setWarnVisible(false);
    setExtendedNotice(`The client extended this call by ${minutes} minutes.`);
  };

  // Re-read the call's length from the server (case-details includes any paid
  // extension). The socket `call-extended` event is the fast path; this is the
  // fallback so a dropped relay can't end an extended call on the old time.
  // Resolves true if it found minutes this screen didn't have yet.
  const syncDurationFromServer = async () => {
    try {
      const res = await Promise.race([
        lawyerService.getCallCaseDetails(call.callId),
        new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);
      const serverMinutes = Number(res?.data?.data?.caseDetails?.duration_minutes);
      const base = Number(call.durationMinutes) || 0;
      const extra = serverMinutes - base - extraRef.current;
      if (base > 0 && extra > 0 && !endedRef.current) {
        applyExtension(extra);
        return true;
      }
    } catch {
      // unreachable — fall through and let the caller decide
    }
    return false;
  };

  const end = (reason) => {
    if (endedRef.current) return;
    endedRef.current = true;
    onEnd(reason);
  };
  const endRef = useRef(end);
  endRef.current = end;

  const total = ((call.durationMinutes || 0) + extraMinutes) * 60;
  const remaining = total > 0 ? Math.max(0, total - seconds) : null;
  const warnAt = total > 6 * 60 ? 5 * 60 : 60;
  const lowTime = remaining !== null && remaining <= warnAt;
  const warnMinutes = Math.round(warnAt / 60);

  // The client stepped away (a mobile client's phone rang, or they pressed Hold).
  useEffect(() => {
    const offs = [
      socketService.subscribe("call-held", (p) => {
        if (!p?.callId || String(p.callId) === String(call.callId)) setHeldByThem(true);
      }),
      socketService.subscribe("call-resumed", (p) => {
        if (!p?.callId || String(p.callId) === String(call.callId)) setHeldByThem(false);
      }),
      // The client paid to extend this free call — without this the plan's own
      // countdown would end the call for both of you on the original time.
      socketService.subscribe("call-extended", (p) => {
        if (String(p?.callId) !== String(call.callId) || endedRef.current) return;
        const minutes = Number(p.minutes) || 0;
        if (minutes <= 0) return;
        // one extension per call; ignore a duplicate relay
        applyExtension(minutes);
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [call.callId]);

  // A held call is dark and silent both ways.
  useEffect(() => {
    setAway(heldByThem);
  }, [heldByThem, setAway]);

  // Talk-time clock; paused while the client is away so they aren't billed for it.
  useEffect(() => {
    if (status !== "connected" || heldByThem) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status, heldByThem]);

  useEffect(() => {
    if (remaining === null || warnedRef.current) return undefined;
    if (remaining <= warnAt && remaining > 0) {
      warnedRef.current = true;
      setWarnVisible(true);
      const t = setTimeout(() => setWarnVisible(false), 7000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [remaining, warnAt]);

  useEffect(() => {
    if (!extendedNotice) return undefined;
    const t = setTimeout(() => setExtendedNotice(null), 4000);
    return () => clearTimeout(t);
  }, [extendedNotice]);

  // Final stretch: poll for an extension every 20s so it lands (and the
  // warning clears) before 0:00, not only at it.
  useEffect(() => {
    if (remaining === null || remaining <= 0 || remaining > 120 || remaining % 20 !== 0 || extraRef.current > 0) return;
    syncDurationFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  // The paid plan ran out — confirm with the server first, in case the client
  // extended the call and the socket relay never arrived.
  useEffect(() => {
    if (!(total > 0 && remaining === 0) || expiryCheckRef.current || endedRef.current) return;
    expiryCheckRef.current = true;
    syncDurationFromServer().then((extended) => {
      expiryCheckRef.current = false;
      if (!extended) endRef.current("timeout");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, total]);

  // The client left the channel. Give them a moment to come back (a reload or a
  // network blip) before closing the call.
  // Prefer whoever is sending video — a stale second uid (an app that rejoined)
  // must not hide the live one.
  const remote = remoteUsers.find((u) => u.videoTrack) || remoteUsers[0];
  useEffect(() => {
    if (remote) {
      seenRemoteRef.current = true;
      return undefined;
    }
    if (!seenRemoteRef.current) return undefined;
    const t = setTimeout(() => endRef.current("left"), LEFT_GRACE_MS);
    return () => clearTimeout(t);
  }, [remote]);

  // Closing or reloading the tab ends the call: there is no way back into the
  // channel, and the client would otherwise wait for a lawyer who is gone.
  useEffect(() => {
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onHide = () => {
      if (endedRef.current) return;
      endedRef.current = true;
      socketService.emit("call-end", { callId: call.callId });
      reportEndOnUnload(call.callId);
    };
    window.addEventListener("beforeunload", warn);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("beforeunload", warn);
      window.removeEventListener("pagehide", onHide);
    };
  }, [call.callId]);

  useEffect(() => {
    if (!spkMenuOpen) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest(".lp-spk-wrap")) setSpkMenuOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [spkMenuOpen]);

  // Play the self-view imperatively; the track's lifecycle belongs to useAgora.
  useEffect(() => {
    const el = localRef.current;
    if (!el || !localVideoTrack || !camOn) return;
    try {
      localVideoTrack.play(el);
    } catch {
      /* not ready yet — retried on next render */
    }
  }, [localVideoTrack, camOn]);

  const remoteVideoTrack = remote?.videoTrack;
  const remoteCamOff = !!remote && !remoteVideoTrack;
  const client = call.user || {};
  const cd = call.caseDetails || {};
  const hasCase = !!(cd.category || cd.jurisdiction || cd.description || cd.plan);

  return (
    <div className="lp-call-screen lwp-incall">
      <div className="lp-call-stage">
        <div className="lp-remote">
          {remoteVideoTrack && !heldByThem ? (
            <VideoPlayer track={remoteVideoTrack} className="lp-remote-video" />
          ) : (
            <div className="lp-remote-placeholder">
              <div className="lp-ph-avatar">
                {client.picture ? <img src={client.picture} alt={client.name} /> : <i className="bi bi-person-fill" />}
              </div>
              <div className="lp-ph-text">
                {status === "failed"
                  ? error || "Could not start the call."
                  : status !== "connected"
                  ? "Connecting…"
                  : !remote && seenRemoteRef.current
                  ? "The client left the call. Waiting for them to reconnect…"
                  : remoteCamOff
                  ? "Client turned their camera off"
                  : "Connecting client's video…"}
              </div>
            </div>
          )}
        </div>

        {heldByThem && (
          <div className="lwp-incall-veil">
            <span className="lwp-incall-veil-icon">
              <i className="bi bi-pause-fill" />
            </span>
            <strong>{client.name || "The client"} is away</strong>
            <p>They have put the call on hold. The timer is paused — please wait.</p>
          </div>
        )}

        <div className="lp-call-top">
          <span className={lowTime ? "lp-timer lp-timer-low" : "lp-timer"}>
            {remaining !== null ? fmt(remaining) : fmt(seconds)}
          </span>
          <span className="lp-enc">
            <span className="lp-enc-dot" /> Encrypted
          </span>
        </div>

        <img src={brandLogo} alt="Legal Platform" className="lp-call-logo" />

        {extendedNotice && (
          <div className="lp-incall-card lp-incall-card-done">
            <span className="lp-incall-icon lp-incall-icon-light"><i className="bi bi-check-lg" /></span>
            <div className="lp-incall-text">
              <strong>Call Extended</strong>
              <span>{extendedNotice}</span>
            </div>
          </div>
        )}

        {warnVisible && (
          <div className="lp-time-warn">
            <i className="bi bi-clock-history" />
            <span>
              {warnMinutes} {warnMinutes === 1 ? "minute" : "minutes"} remaining
            </span>
          </div>
        )}

        <div className={camOn && localVideoTrack && !heldByThem ? "lp-pip" : "lp-pip lp-pip-off"}>
          <div ref={localRef} className="lp-pip-video" />
          <div className="lp-pip-off-label">
            <i className={heldByThem ? "bi bi-pause-fill" : "bi bi-camera-video-off-fill"} />
            <span>{heldByThem ? "On hold" : camOn ? "Camera unavailable" : "Camera off"}</span>
          </div>
          <span className="lp-pip-dot" />
        </div>

        <div className="lp-call-info">
          <div className="lp-call-name">{client.name || "Client"}</div>
          {(cd.category || cd.plan) && (
            <div className="lp-call-cats">
              {cd.category && <span className="lp-cat-chip">{cd.category}</span>}
              {cd.plan && <span className="lp-cat-chip">{cd.plan}</span>}
            </div>
          )}
        </div>

        {caseOpen && hasCase && (
          <div className="lwp-incall-case">
            <div className="lwp-incall-case-head">
              <strong>Case details</strong>
              <button type="button" onClick={() => setCaseOpen(false)} aria-label="Close case details">
                <i className="bi bi-x-lg" />
              </button>
            </div>
            {cd.category && (
              <p>
                <span>Category</span>
                {cd.category}
              </p>
            )}
            {cd.jurisdiction && (
              <p>
                <span>Jurisdiction</span>
                {cd.jurisdiction}
              </p>
            )}
            {cd.plan && (
              <p>
                <span>Plan</span>
                {cd.plan}
              </p>
            )}
            {cd.description && (
              <p>
                <span>Description</span>
                {cd.description}
              </p>
            )}
          </div>
        )}

        <div className="lp-call-controls">
          <div className="lp-call-ctrl">
            <button className={micOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleMic} disabled={heldByThem}>
              <i className={micOn ? "bi bi-mic-fill" : "bi bi-mic-mute-fill"} />
            </button>
            <span className="lp-call-ctrl-label">Mute</span>
          </div>
          <div className="lp-call-ctrl">
            <button className={camOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleCam} disabled={heldByThem}>
              <i className={camOn ? "bi bi-camera-video-fill" : "bi bi-camera-video-off-fill"} />
            </button>
            <span className="lp-call-ctrl-label">Camera</span>
          </div>
          {canFlip && (
            <div className="lp-call-ctrl">
              <button className="lp-call-btn" onClick={flipCamera} disabled={!camOn || heldByThem}>
                <i className="bi bi-arrow-repeat" />
              </button>
              <span className="lp-call-ctrl-label">Flip</span>
            </div>
          )}
          <div className="lp-call-ctrl">
            <div className="lp-spk-wrap">
              <button className={speakerOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleSpeaker} disabled={heldByThem}>
                <i className={speakerOn ? "bi bi-volume-up-fill" : "bi bi-volume-mute-fill"} />
              </button>
              {playbackDevices.length > 1 && (
                <button className="lp-spk-caret" onClick={() => setSpkMenuOpen((o) => !o)} title="Choose audio output">
                  <i className="bi bi-chevron-up" />
                </button>
              )}
              {spkMenuOpen && (
                <div className="lp-spk-menu">
                  <div className="lp-spk-menu-head">Speaker output</div>
                  {playbackDevices.map((d) => (
                    <button
                      key={d.deviceId}
                      className={d.deviceId === activeSpeaker ? "lp-spk-item lp-spk-item-on" : "lp-spk-item"}
                      onClick={() => {
                        setPlayback(d.deviceId);
                        setSpkMenuOpen(false);
                      }}
                    >
                      <i className="bi bi-check2" />
                      <span>{d.label || "Output device"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="lp-call-ctrl-label">Speaker</span>
          </div>
          {hasCase && (
            <div className="lp-call-ctrl">
              <button className={caseOpen ? "lp-call-btn lp-active" : "lp-call-btn"} onClick={() => setCaseOpen((o) => !o)}>
                <i className="bi bi-file-earmark-text-fill" />
              </button>
              <span className="lp-call-ctrl-label">Case</span>
            </div>
          )}
        </div>

        <div className="lp-call-secure">
          <i className="bi bi-shield-lock-fill" />
          <div className="lp-call-secure-text">
            <strong>Your call is secure and confidential</strong>
            <span>The call ends when the client hangs up or the plan time runs out</span>
          </div>
        </div>
      </div>
    </div>
  );
}
